import { cn } from "@/lib/utils";
import { AlertTriangle, RotateCcw } from "lucide-react";
import { Component, ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error) {
    // 記錄錯誤但使用簡潔的訊息
    console.log("[ErrorBoundary] Caught error:", error.message);
  }

  render() {
    if (this.state.hasError) {
      // 檢查是否為 DOM 操作錯誤（通常由瀏覽器擴充功能導致）
      const isDOMError = this.state.error?.message?.includes("removeChild") || 
                         this.state.error?.message?.includes("insertBefore") ||
                         this.state.error?.message?.includes("appendChild");

      return (
        <div className="flex items-center justify-center min-h-screen p-8 bg-gradient-to-br from-slate-50 to-slate-100">
          <div className="flex flex-col items-center w-full max-w-md p-8 text-center">
            <AlertTriangle
              size={48}
              className="text-amber-500 mb-6 flex-shrink-0"
            />

            <h2 className="text-xl font-semibold text-gray-800 mb-4">發生了意外錯誤.</h2>

            {isDOMError ? (
              <p className="text-gray-600 mb-6">
                這可能是由瀏覽器擴充功能(如翻譯工具)導致的.
                請嘗試重新載入頁面或暫時停用擴充功能.
              </p>
            ) : (
              <div className="p-4 w-full rounded bg-slate-100 overflow-auto mb-6">
                <pre className="text-sm text-slate-600 whitespace-break-spaces text-left">
                  {this.state.error?.message}
                </pre>
              </div>
            )}

            <button
              onClick={() => window.location.reload()}
              className={cn(
                "flex items-center gap-2 px-6 py-3 rounded-lg",
                "bg-emerald-600 text-white",
                "hover:bg-emerald-700 cursor-pointer transition-colors"
              )}
            >
              <RotateCcw size={16} />
              重新載入頁面
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
